import { BookingV4, Preorder, Item } from "../../types/bookings";
import { postUpdateBookingV4 } from "./UpdateBooking";

const generateId = () => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

export type PreOrderItemFormData = {
  itemId: string;
  itemName: string;
  itemPrice: number;
  itemType: string;
  itemSubType: string;
  forName?: string;
  seat?: string;
  time?: string;
  quantity: number;
  discountType?: "percentage" | "fixed";
  discountAmount?: number;
  description?: string;
};

/**
 * Add a new pre-order item to a booking
 */
export const addPreOrderItem = async (
  booking: BookingV4,
  itemData: PreOrderItemFormData
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const userName = "Admin";

    const baseTotal = itemData.quantity * itemData.itemPrice;
    let discountValue = 0;

    if (itemData.discountAmount) {
      if (itemData.discountType === "percentage") {
        discountValue = (baseTotal * itemData.discountAmount) / 100;
      } else {
        discountValue = itemData.discountAmount;
      }
    }

    const itemTotal = baseTotal - discountValue;

    const newItem: Item = {
      id: generateId(),
      name: itemData.itemName,
      quantity: itemData.quantity,
      total: itemTotal,
      discount_amount: itemData.discountAmount,
      discount_type: itemData.discountType,
      discount_value: discountValue,
      type: itemData.itemType,
      sub_type: itemData.itemSubType,
      time: itemData.time,
      plu: 0,
      price: itemData.itemPrice,
      base_price: itemData.itemPrice,
      options_price: 0,
      for_name: itemData.forName ?? "",
      seat: itemData.seat,
      items: [],
      description: itemData.description,
      surcharge: null
    };

    const newPreOrder: Preorder = {
      id: generateId(),
      created_date: new Date(),
      name: userName,
      email: booking.email ?? "",
      items: [newItem],
      total_cost: itemTotal,
      amount_paid: 0,
      source: "Admin",
      status: "complete",
      service_charge_percentage: 0,
      service_charge_amount: 0,
      total_discount: discountValue
    };

    const updatedPreOrders = [...(booking.preorders || []), newPreOrder];

    const updatedBooking: BookingV4 = {
      ...booking,
      preorders: updatedPreOrders
    };

    const savedBooking = await postUpdateBookingV4(updatedBooking);

    if (!savedBooking) {
      return { error: "Failed to save pre-order item" };
    }

    return { booking: savedBooking };
  } catch (error) {
    console.error("Error adding pre-order item:", error);
    return { error: "Failed to add pre-order item" };
  }
};

/**
 * Update an existing pre-order item
 */
export const updatePreOrderItem = async (
  booking: BookingV4,
  preOrderId: string,
  itemData: PreOrderItemFormData
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const baseTotal = itemData.quantity * itemData.itemPrice;
    let discountValue = 0;

    if (itemData.discountAmount) {
      if (itemData.discountType === "percentage") {
        discountValue = (baseTotal * itemData.discountAmount) / 100;
      } else {
        discountValue = itemData.discountAmount;
      }
    }

    const itemTotal = baseTotal - discountValue;

    const updatedPreOrders = booking.preorders?.map((preOrder) => {
      if (preOrder.id === preOrderId) {
        const updatedItem: Item = {
          ...preOrder.items[0],
          name: itemData.itemName,
          quantity: itemData.quantity,
          total: itemTotal,
          discount_amount: itemData.discountAmount,
          discount_type: itemData.discountType,
          discount_value: discountValue,
          type: itemData.itemType,
          sub_type: itemData.itemSubType,
          time: itemData.time,
          price: itemData.itemPrice,
          base_price: itemData.itemPrice,
          for_name: itemData.forName ?? "",
          seat: itemData.seat,
          description: itemData.description
        };

        return {
          ...preOrder,
          items: [updatedItem],
          total_cost: itemTotal,
          total_discount: discountValue
        };
      }
      return preOrder;
    });

    const updatedBooking: BookingV4 = {
      ...booking,
      preorders: updatedPreOrders
    };

    const savedBooking = await postUpdateBookingV4(updatedBooking);

    if (!savedBooking) {
      return { error: "Failed to update pre-order item" };
    }

    return { booking: savedBooking };
  } catch (error) {
    console.error("Error updating pre-order item:", error);
    return { error: "Failed to update pre-order item" };
  }
};

/**
 * Delete a pre-order item
 */
export const deletePreOrderItem = async (
  booking: BookingV4,
  preOrderId: string
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const updatedPreOrders = booking.preorders?.map((preOrder) => {
      if (preOrder.id === preOrderId) {
        return {
          ...preOrder,
          status: "deleted"
        };
      }
      return preOrder;
    });

    const updatedBooking: BookingV4 = {
      ...booking,
      preorders: updatedPreOrders
    };

    const savedBooking = await postUpdateBookingV4(updatedBooking);

    if (!savedBooking) {
      return { error: "Failed to delete pre-order item" };
    }

    return { booking: savedBooking };
  } catch (error) {
    console.error("Error deleting pre-order item:", error);
    return { error: "Failed to delete pre-order item" };
  }
};

/**
 * Update service charge for a specific pre-order
 */
export const updateServiceCharge = async (
  booking: BookingV4,
  preOrderId: string,
  serviceChargePercentage: number
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const updatedPreOrders = booking.preorders?.map((preOrder) => {
      if (preOrder.id === preOrderId) {
        const serviceChargeAmount =
          (preOrder.total_cost * serviceChargePercentage) / 100;

        return {
          ...preOrder,
          service_charge_percentage: serviceChargePercentage,
          service_charge_amount: serviceChargeAmount
        };
      }
      return preOrder;
    });

    const updatedBooking: BookingV4 = {
      ...booking,
      preorders: updatedPreOrders
    };

    const savedBooking = await postUpdateBookingV4(updatedBooking);

    if (!savedBooking) {
      return { error: "Failed to update service charge" };
    }

    return { booking: savedBooking };
  } catch (error) {
    console.error("Error updating service charge:", error);
    return { error: "Failed to update service charge" };
  }
};

/**
 * Update customer notes for a specific pre-order
 */
export const updatePreOrderComments = async (
  booking: BookingV4,
  preOrderId: string,
  comments: string
): Promise<{ booking?: BookingV4; error?: string }> => {
  try {
    const updatedPreOrders = booking.preorders?.map((preOrder) => {
      if (preOrder.id === preOrderId) {
        return {
          ...preOrder,
          customer_notes: comments
        };
      }
      return preOrder;
    });

    const updatedBooking: BookingV4 = {
      ...booking,
      preorders: updatedPreOrders
    };

    const savedBooking = await postUpdateBookingV4(updatedBooking);

    if (!savedBooking) {
      return { error: "Failed to update pre-order comments" };
    }

    return { booking: savedBooking };
  } catch (error) {
    console.error("Error updating pre-order comments:", error);
    return { error: "Failed to update pre-order comments" };
  }
};
